import { useEffect, useMemo, useCallback, useRef } from 'react';

import { debounce } from 'lodash';

import { getComposeDraft, setComposeDraft, clearComposeDraft } from '../../../../../../utils/appStorage';
import type { ComposeType, SerializableAttachment, Attachment } from '../types';

const DEBOUNCE_DELAY = 500;

export const toSerializableAttachment = async(
  attachment: Attachment,
): Promise<SerializableAttachment | null> => {
  if (attachment.dataURL) {
    return {
      dataURL: attachment.dataURL,
      fileName: attachment.file?.name || 'attachment',
      fileType: attachment.file?.type || 'application/octet-stream',
      fileSize: attachment.file?.size || 0,
    };
  }

  if (attachment.file) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve({
          dataURL: reader.result as string,
          fileName: attachment.file!.name,
          fileType: attachment.file!.type,
          fileSize: attachment.file!.size,
        });
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(attachment.file);
    });
  }

  return null;
};

export const fromSerializableAttachment = (attachment: SerializableAttachment): Attachment => {
  return {
    dataURL: attachment.dataURL,
    file: undefined,
  };
};

export const serializeAttachments = async(
  attachments: Attachment[],
): Promise<SerializableAttachment[]> => {
  const results = await Promise.all(attachments.map(toSerializableAttachment));
  return results.filter((a): a is SerializableAttachment => a !== null);
};

export const deserializeAttachments = (attachments: SerializableAttachment[]): Attachment[] => {
  return attachments.map(fromSerializableAttachment);
};

type UseComposeDraftOptions<T> = {
  type: ComposeType;
  customerId: number | undefined;
  getCurrentDraft: () => Omit<T, 'updatedAt'>;
  onDraftLoaded: (draft: T) => void;
  isDraftEmpty: (draft: Omit<T, 'updatedAt'>) => boolean;
  skipInitialLoad?: boolean;
};

export const useComposeDraft = <T extends {updatedAt: number}>({
  type,
  customerId,
  getCurrentDraft,
  onDraftLoaded,
  isDraftEmpty,
  skipInitialLoad = false,
}: UseComposeDraftOptions<T>) => {
  const hasLoadedRef = useRef(false);
  const lastCustomerIdRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (lastCustomerIdRef.current !== customerId) {
      hasLoadedRef.current = false;
      lastCustomerIdRef.current = customerId;
    }

    if (!customerId || hasLoadedRef.current || skipInitialLoad) return;

    const savedDraft = getComposeDraft<T>(type, customerId);
    if (savedDraft) {
      onDraftLoaded(savedDraft);
    }
    hasLoadedRef.current = true;
  }, [customerId, type, onDraftLoaded, skipInitialLoad]);

  const debouncedSave = useMemo(
    () =>
      debounce((id: number, draft: Omit<T, 'updatedAt'>) => {
        const fullDraft = { ...draft, updatedAt: Date.now() } as T;
        setComposeDraft(type, id, fullDraft);
      }, DEBOUNCE_DELAY),
    [type],
  );

  useEffect(() => {
    return () => {
      debouncedSave.cancel();
    };
  }, [debouncedSave]);

  const saveDraft = useCallback(() => {
    if (!customerId) return;
    const draft = getCurrentDraft();
    if (isDraftEmpty(draft)) {
      debouncedSave.cancel();
      clearComposeDraft(type, customerId);
    } else {
      debouncedSave(customerId, draft);
    }
  }, [customerId, getCurrentDraft, isDraftEmpty, debouncedSave, type]);

  const clearDraft = useCallback(() => {
    if (!customerId) return;
    debouncedSave.cancel();
    clearComposeDraft(type, customerId);
  }, [customerId, type, debouncedSave]);

  return { saveDraft, clearDraft };
};
